import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  NotificationCenter,
  NotificationType,
  NotificationPriority,
  NotificationApiResponse,
  PaginatedNotificationResponse,
  UnreadCountResponse
} from '../interfaces/notification';
import { HttpService } from './http.service';
import { LanguageService } from "./language.service";

const BASE_PATH = 'v2/extranet/notifications';

/**
 * Maps known backend notification title strings to frontend translation keys.
 * Keys are lowercase for case-insensitive matching.
 * Add new entries here when the backend introduces new notification types.
 */
const NOTIFICATION_ACTION_LABEL_MAP: Record<string, string> = {
  'view hotel': 'HEADER.NOTIFICATIONS.VIEW_HOTEL',
  'view booking': 'HEADER.NOTIFICATIONS.VIEW_BOOKING',
};

const NOTIFICATION_TITLE_KEY_MAP: Record<string, string> = {
  'new booking received': 'notifications.titles.new_booking_received',
  'booking cancelled': 'notifications.titles.booking_cancelled',
  'booking canceled': 'notifications.titles.booking_cancelled',
  'booking confirmed': 'notifications.titles.booking_confirmed',
  'booking updated': 'notifications.titles.booking_updated',
  'new hotel partner request': 'notifications.titles.new_hotel_partner_request',
  'hotel status updated': 'notifications.titles.hotel_status_updated',
  'hotel information updated': 'notifications.titles.hotel_information_updated',
  'hotel settings updated': 'notifications.titles.hotel_settings_updated',
  'password changed': 'notifications.titles.password_changed',
  'login from new device': 'notifications.titles.login_from_new_device',
  'system maintenance': 'notifications.titles.system_maintenance',
};

@Injectable({
  providedIn: 'root'
})
export class NotificationCenterApiService {
  private readonly http = inject(HttpService);
  private readonly languageService = inject(LanguageService);

  /**
   * Fetches a paginated list of notifications filtered by tab, type, read status, and page.
   */
  getNotifications(
    tab: 'active' | 'archived' = 'active',
    page = 1,
    perPage = 10,
    filters?: { type?: NotificationType; isRead?: boolean }
  ): Observable<{ notifications: NotificationCenter[]; total: number; page: number; perPage: number; lastPage: number }> {
    const params: Record<string, string> = {
      tab,
      locale: this.languageService.getCurrentLanguage(),
      page: page.toString(),
      per_page: perPage.toString()
    };

    if (filters?.type) {
      params['type'] = filters.type;
    }

    if (filters?.isRead !== undefined) {
      params['is_read'] = filters.isRead ? '1' : '0';
    }

    return this.http.get<PaginatedNotificationResponse>(BASE_PATH, { params }).pipe(
      map(response => ({
        notifications: response.data.map(item => this.mapApiToNotification(item)),
        total: response.meta.total,
        page: response.meta.current_page,
        perPage: response.meta.per_page,
        lastPage: response.meta.last_page
      }))
    );
  }

  /**
   * Fetches a limited flat list of notifications for the bell dropdown.
   */
  getNotificationsLimited(limit = 5): Observable<NotificationCenter[]> {
    return this.http.get<NotificationApiResponse[]>(BASE_PATH, {
      params: {
        limit: limit.toString(),
        locale: this.languageService.getCurrentLanguage(),
      }
    }).pipe(
      map(items => items.map(item => this.mapApiToNotification(item)))
    );
  }

  /**
   * Fetches the current unread notification count. Polled every 30 seconds.
   */
  getUnreadCount(): Observable<number> {
    return this.http.get<UnreadCountResponse>(`${BASE_PATH}/unread-count`).pipe(
      map(response => response.unread_count)
    );
  }

  /**
   * Marks a single notification as read.
   */
  markAsRead(uuid: string): Observable<NotificationCenter> {
    return this.http.patch<NotificationApiResponse>(`${BASE_PATH}/${uuid}/read`, {}).pipe(
      map(item => this.mapApiToNotification(item))
    );
  }

  /**
   * Marks all active notifications as read.
   */
  markAllAsRead(): Observable<void> {
    return this.http.patch<void>(`${BASE_PATH}/read-all`, {});
  }

  /**
   * Archives a notification (moves it to the archived tab).
   */
  archiveNotification(uuid: string): Observable<void> {
    return this.http.patch<void>(`${BASE_PATH}/${uuid}/archive`, {});
  }

  /**
   * Permanently deletes a notification.
   */
  deleteNotification(uuid: string): Observable<void> {
    return this.http.delete<void>(`${BASE_PATH}/${uuid}`);
  }

  /**
   * Tracks that the user clicked the CTA (action button) on a notification.
   */
  trackCtaClick(uuid: string): Observable<void> {
    return this.http.patch<void>(`${BASE_PATH}/${uuid}/cta-clicked`, {});
  }

  /**
   * Maps a snake_case API response to the camelCase NotificationCenter interface.
   */
  private mapApiToNotification(item: NotificationApiResponse): NotificationCenter {
    const titleKey = item.title
      ? NOTIFICATION_TITLE_KEY_MAP[item.title.toLowerCase().trim()]
      : undefined;

    return {
      uuid: item.id,
      title: item.title,
      titleKey,
      body: item.body,
      type: item.type as NotificationType,
      priority: item.priority as NotificationPriority,
      isRead: item.is_read,
      createdAt: item.created_at,
      actionUrl: item.action_url ?? undefined,
      actionLabel: item.action_label ? NOTIFICATION_ACTION_LABEL_MAP[item.action_label.toLowerCase().trim()] : undefined,
      hotel: item.hotel,
      deadlineAt: item.deadline_at ?? undefined,
      timeAgo: item.time_ago
    };
  }
}
